import { EDPElevatorBank } from '../floor-container/elevator-banks/elevator-banks.model';
import { EDPFloorSpaceInfo } from '../floor-container/floor/floor.model';

export type FloorUpdateAction = 'floor-info' | 'floor-number' | 'floor-info-bulk';

export interface FloorsToUpdate {
  withFloorNumbersToUpdate: EDPFloorSpaceInfo[];
  withFloorInfoToUpdateOrCreate: EDPFloorSpaceInfo[];
  withFloorNumberAndFloorInfoToUpdate: EDPFloorSpaceInfo[];
}

export interface Toolbar {
  floors?: EDPFloorSpaceInfo[];
  totalNumberOfFloors: number;
  netRentableArea: number;
  netRentableAreaUOM: string;
  totalGrossArea?: number;
  totalGrossAreaUOM?: string;
  totalGrossOfficeArea?: number;
  totalGrossOfficeAreaUOM?: string;
  unknownIssues?: number;
  expiredIssues?: number;
  underGroundFloors?: number;
  commonFloorSize: number;
  commonFloorSizeUOM: string;
  minimumFloorArea?: number;
  minimumFloorAreaUOM?: string;
  maximumFloorArea?: number;
  maximumFloorAreaUOM?: string;
  totalVacancyArea?: number;
  totalVacancyAreaUOM?: string;
}

export class ReqFloorPlan {
  'request.source_id': number;

  // tslint:disable-next-line:variable-name
  constructor(
    public property_id: number,
    public floor_plan_id: number,
    public floor_id: number
  ) {
    this['request.source_id'] = property_id;
  }
}

export class ReqFloorInfo {
  'request.source_id': number;

  static AttributesMap = {
    floor_number: 'property_space.floor_number',
    floor_index: 'property_space.floor_index',
    floor_id: 'property_space.floor_id',
    floor_plan_url: 'property_space.floor_plan_url',
    floor_desc: 'property_space.floor_desc',
    hide_floor_f: 'property_space.hide_floor_f',
    floor_size: 'property_space.floor_size',
    floor_size_uom_desc: 'property_space.floor_size_uom_desc',
    floor_slab_height: 'property_space.floor_slab_height',
    floor_slab_height_uom: 'property_space.floor_slab_height_uom',
    total_gross_area: 'property_space.total_gross_area',
    total_gross_area_uom_desc: 'property_space.total_gross_area_uom_desc',
  };

  constructor(
    // tslint:disable-next-line:variable-name
    public property_id: number,
    floor: EDPFloorSpaceInfo,
    updateAction: FloorUpdateAction = 'floor-info'
  ) {
    if (floor == null) {
      throw new Error(`Floor argument is null on ReqFloorInfo Constructor for property_id: ${property_id}`);
    }
    this['request.source_id'] = property_id;
    Object.entries(floor).forEach(([key, value]) => {
      const attribute = ReqFloorInfo.AttributesMap[key];
      if (attribute != null) {
        this[attribute] = value;
      }
    });

    if (updateAction === 'floor-number') {
      this['floor_id'] = floor.floor_id;
      this['floor_number'] = floor.floor_number;
      this['floor_index'] = floor.floor_index;
      delete this['property_space.floor_number'];
    }
  }
}

export class ReqPropertyInfo {
  'request.source_id': number;

  // tslint:disable-next-line:variable-name
  constructor(
    public property_id: number,
    undergroundFloors: number,
    totalNumberOfFloors: number,
    netRentableArea: number,
    NRAUom: string,
    commonFloorSize: number,
    commonFloorSizeUOM: string
  ) {
    this['request.source_id'] = property_id;
    this['property.number_of_floor_below_ground'] = undergroundFloors;
    this['property.total_number_of_floors'] = totalNumberOfFloors;
    this['property.net_rentable_area'] = netRentableArea;
    this['property.net_rentable_area_uom_desc'] = NRAUom;
    this['property.typical_floor_area'] = commonFloorSize;
    this['property.typical_floor_area_uom_desc'] = commonFloorSizeUOM;
  }
}

// tslint:disable:variable-name
class ReqElevatorInfo {
  request = { source_id: null };
  constructor(
    public property_id: number,
    elevator: EDPElevatorBank
  ) {
    if (elevator == null) {
      throw new Error(`elevator argument is null on ReqElevatorInfo Constructor for property_id: ${property_id}`);
    }
    this.request.source_id = property_id;
  }
}

export class ReqUpdateElevatorInfo extends ReqElevatorInfo {
  static AttributesMap = {
    elevator_type_desc: 'elevator_type_desc',
    elevator_bank_name: 'property_elevator_bank_name',
    start_floor: 'elevator_bank_low_floor_number',
    end_floor: 'elevator_bank_high_floor_number',
    elevator_bank_notes: 'property_elevator_bank_notes',
    elevator_bank_capacity: 'elevator_bank_capacity',
    elevator_bank_capacity_uom: 'elevator_bank_capacity_uom',
    ada_compliant_f: 'ada_compliant_f',
  };

  property_elevator_bank_id: number;
  property_elevator_bank = {};

  constructor(
    public property_id: number,
    elevator: EDPElevatorBank
  ) {
    super(property_id, elevator);
    this.property_elevator_bank_id = elevator.id;
    Object.entries(elevator)
      .filter(([, val]) => val != null)
      .forEach(([key, value]) => {
        const attribute = ReqUpdateElevatorInfo.AttributesMap[key];
        if (attribute != null) {
          this.property_elevator_bank[attribute] = value;
        }
      });
  }
}

export class ReqCreateElevatorInfo extends ReqElevatorInfo {
  static AttributesMap = {
    elevator_type_desc: 'elevator_type_desc',
    elevator_bank_name: 'elevator_bank_name',
    total_number_of_elevators: 'total_number_of_elevators',
    start_floor: 'start_floor',
    end_floor: 'end_floor',
    elevator_bank_notes: 'elevator_bank_notes',
    elevator_bank_capacity: 'elevator_bank_capacity',
    elevator_bank_capacity_uom: 'elevator_bank_capacity_uom',
    ada_compliant_f: 'ada_compliant_f',
  };

  elevator_bank = {};

  constructor(
    public property_id: number,
    elevator: EDPElevatorBank
  ) {
    super(property_id, elevator);
    Object.entries(elevator)
      .filter(([, val]) => val != null)
      .forEach(([key, value]) => {
        const attribute = ReqCreateElevatorInfo.AttributesMap[key];
        if (attribute != null) {
          this.elevator_bank[attribute] = value;
        }
      });
  }
}
// tslint:enable:variable-name

export class ReqDeleteElevators {
  'request.source_id': number;
  // tslint:disable-next-line:variable-name
  constructor(
    public property_id: number,
    public elevator_bank_id: number
  ) {
    this['request.source_id'] = property_id;
  }
}

export interface UpdateElevatorInfoToEDP {
  'request.source_id': number;
  property_id: number;
  'elevator_bank.id': number;
  'elevator_bank.elevator_type_desc'?: string;
  'elevator_bank.elevator_bank_name'?: number;
  'elevator_bank.total_number_of_elevators'?: string;
  'elevator_bank.start_floor'?: string;
  'elevator_bank.end_floor'?: number;
  'elevator_bank.elevator_bank_notes'?: string;
  'elevator_bank.elevator_bank_capacity'?: number;
  'elevator_bank.elevator_bank_capacity_uom'?: string;
  'elevator_bank.ada_compliant_f'?: boolean;
}

export interface DeleteElevatorFromEDP {
  'request.source_id': number;
  property_id: number;
  elevator_bank_id: number;
}

export interface UpdateElevatorEdpRes {
  data: UpdateElevatorInfoToEDP[];
  entityID: number;
}

export interface DeleteElevatorEDPRes {
  data: DeleteElevatorFromEDP[];
  entityID: number;
}

export interface ElevatorBankHeader {
  field: string;
  header: string;
  required?: boolean;
  headerClass: string;
  colClass: string;
}
